import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { FormikPhoneInput } from '@/components/form/fields/FormikPhoneInput/FormikPhoneInput';
import { Button } from '@/components/Button/Button';
import { useFormik } from 'formik';
import { ButtonGroup } from '@/components/Button/ButtonGroup/ButtonGroup';
import { useMutation } from '@tanstack/react-query';
import { editInternalOrganizationUserMobilePhone } from '@/bundle/_MasterAdmin/_OrganizationUserDetailsPage/api/api';
import { showSnackbar } from '@/components/Snackbar/Snackbar';
import { getChangedFormValues } from '@/helpers/formHelpers/formHelpers';
import { useParams } from 'react-router';
import { EditInternalOrganizationUserMobilePhonePayloadType } from '@/api/v1/organization/internal/editInternalOrganizationUserMobilePhoneApi';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { MOBILE_PHONE_VALIDATION_SCHEMA } from '@/const/formConfigs';

type EditMobilePhoneFormType = {
  mobilePhone: string;
  onClose: () => void;
  onRefetchUser: () => void;
};

export const EditMobilePhoneForm = ({ mobilePhone, onClose, onRefetchUser }: EditMobilePhoneFormType) => {
  const { id } = useParams<{ id: string }>();
  const { data, mutate, isPending } = useMutation({
    mutationKey: ['edit_internal_organization_user_mobile_phone'],
    mutationFn: (values: EditInternalOrganizationUserMobilePhonePayloadType) => {
      return editInternalOrganizationUserMobilePhone(id, values);
    },
    onSuccess(response) {
      if (response?.error) return;

      showSnackbar('Mobile phone number has been updated');
      onRefetchUser();
      onClose();
    },
  });

  const formik = useFormik({
    initialValues: {
      mobile_phone: mobilePhone,
    },
    validationSchema: MOBILE_PHONE_VALIDATION_SCHEMA,
    onSubmit: (values) => {
      const payload = getChangedFormValues<EditInternalOrganizationUserMobilePhonePayloadType>(
        values,
        formik.initialValues
      );

      if (!payload) {
        return onClose();
      }

      mutate(payload);
    },
  });

  const submitFormOnEnter = () => {
    formik.handleSubmit();
  };

  const formError = getResponseError(data?.error);

  return (
    <FormikForm value={formik} error={formError}>
      <FormikPhoneInput
        name='mobile_phone'
        label='Mobile Phone Number*'
        placeholder='Enter Mobile Phone'
        apiError={data?.error}
        onEnterKeyPress={submitFormOnEnter}
      />
      <ButtonGroup marginTop='12px'>
        <Button type='button' variant='secondary' onClick={onClose}>
          Cancel
        </Button>
        <Button type='submit' isLoading={isPending}>
          Save
        </Button>
      </ButtonGroup>
    </FormikForm>
  );
};
